import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { vaultService } from '../../services/vaultService';
import { manageWarbandService } from '../../services/manageWarbandService';
import { Spell, WarbandLevelUp } from '../../types';
import { computeUnusedLevelUps } from '../../utils/formatting';
import { SpellCard } from '../shared/SpellCard';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';

interface AddSpellModalProps {
  fromGrimoire?: boolean;
}

export const AddSpellModal: React.FC<AddSpellModalProps> = ({ fromGrimoire = false }) => {
  const {
    ui,
    selectedWarbandId,
    spellcastersAndSpells,
    closeAddSpellModal,
    setVaultItems,
  } = useWarbandStore();
  const levelUps = useWarbandStore((s) => s.levelUps);
  const setStoreLevelUps = useWarbandStore((s) => s.setLevelUps);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const allAvailableSpells = useWarbandStore((s) => s.allAvailableSpells);
  const [selectedSpellId, setSelectedSpellId] = React.useState('');
  const [selectedSchool, setSelectedSchool] = React.useState('');
  const [addFromGrimoire, setAddFromGrimoire] = React.useState(fromGrimoire);
  const vaultItems = useWarbandStore((s) => s.vaultItems);
  const warbands = useWarbandStore((s) => s.warbands);
  const warband = warbands.find((w) => w.id === selectedWarbandId) ?? null;
  const unusedLevelUps = addFromGrimoire && warband ? computeUnusedLevelUps(warband, levelUps) : 0;

  const availableSpells = React.useMemo(() => {
    const selectedSpellIds = new Set(
      (spellcastersAndSpells?.warbandSpells || []).map((spell) => spell.id)
    );
    const filtered = allAvailableSpells.filter((spell) => !selectedSpellIds.has(spell.id));
    return filtered;
  }, [allAvailableSpells, spellcastersAndSpells]);

  // Keep school selection in sync when availableSpells changes
  React.useEffect(() => {
    const schools = new Set<string>();
    availableSpells.forEach((spell) => {
      const schoolName = spell.school?.trim() || 'Unknown';
      schools.add(schoolName);
    });
    const sorted = Array.from(schools).sort();
    setSelectedSchool((prev) => (sorted.includes(prev) ? prev : sorted[0] || ''));
  }, [availableSpells]);

  React.useEffect(() => {
    setAddFromGrimoire(fromGrimoire);
  }, [fromGrimoire]);

  React.useEffect(() => {
    if (!ui.addSpellModalOpen) return;
    if (warband && computeUnusedLevelUps(warband, levelUps) === 0) {
      setAddFromGrimoire(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.addSpellModalOpen, selectedWarbandId]);

  const availableSchools = React.useMemo(() => {
    const schools = new Set<string>();
    availableSpells.forEach((spell) => {
      const schoolName = spell.school?.trim() || 'Unknown';
      schools.add(schoolName);
    });
    return Array.from(schools).sort((a, b) => a.localeCompare(b));
  }, [availableSpells]);

  const grimoireSpellIds = React.useMemo(() => {
    return new Set(
      vaultItems
        .filter((v) => v.id.startsWith('grimoire_'))
        .map((v) => v.invokedSpellId as string)
    );
  }, [vaultItems]);

  const filteredAvailableSpells = React.useMemo(() => {
    if (!addFromGrimoire) return availableSpells;
    return availableSpells.filter((spell) => grimoireSpellIds.has(spell.id));
  }, [availableSpells, addFromGrimoire, grimoireSpellIds]);

  const groupedSpells = React.useMemo(() => {
    const groups: Record<string, Spell[]> = {};

    const spellsToGroup = addFromGrimoire
      ? filteredAvailableSpells
      : filteredAvailableSpells.filter((spell) => {
          const schoolName = spell.school?.trim() || 'Unknown';
          return schoolName === selectedSchool;
        });

    spellsToGroup.forEach((spell) => {
      const schoolName = addFromGrimoire ? 'Grimoire' : spell.school?.trim() || 'Unknown';
      if (!groups[schoolName]) {
        groups[schoolName] = [];
      }
      groups[schoolName].push(spell);
    });

    return Object.entries(groups)
      .map(([school, spells]) => ({
        school,
        spells: [...spells].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.school.localeCompare(b.school));
  }, [filteredAvailableSpells, selectedSchool, addFromGrimoire]);

  const selectedSpell = React.useMemo(
    () => filteredAvailableSpells.find((spell) => spell.id === selectedSpellId) ?? null,
    [filteredAvailableSpells, selectedSpellId]
  );

  const handleAddSpell = async () => {
    if (!selectedWarbandId || !selectedSpellId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!selectedSpell) {
        setError('Please select a spell');
        return;
      }

      if (addFromGrimoire) {
        if (!warband) throw new Error('Warband data not available');
        const totalSlots = Math.max(0, Math.floor(warband.experience / 100));
        const usedLevels = new Set(
          levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned).map((lu) => lu.level)
        );
        let targetLevel: number | null = null;
        for (let level = 2; level <= totalSlots + 1; level++) {
          if (!usedLevels.has(level)) { targetLevel = level; break; }
        }
        if (targetLevel === null) throw new Error('No level-up slot available to learn from a grimoire');
        const newEntry: WarbandLevelUp = {
          warbandId: selectedWarbandId,
          level: targetLevel,
          statsIncrease: null,
          spellImprovement: null,
          spellLearned: selectedSpellId,
        };
        const filledEntries = levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned);
        const updatedLevelUps = [...filledEntries, newEntry];
        await manageWarbandService.saveLevelUps(selectedWarbandId, {
          experience: warband.experience,
          levelUps: updatedLevelUps,
        });
        setStoreLevelUps(updatedLevelUps);

        const grimoireItem = vaultItems.find((v) => v.invokedSpellId === selectedSpell.id);
        if (grimoireItem?.instanceId) {
          await vaultService.deleteVaultItem(selectedWarbandId, grimoireItem.instanceId);
          setVaultItems(vaultItems.filter((v) => v.instanceId !== grimoireItem.instanceId));
        }
      }

      useDirtyTrackingStore.getState().queueSpellAdd(selectedSpell);

      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to add spell';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedSpellId('');
    setSelectedSchool('');
    setAddFromGrimoire(fromGrimoire);
    setError(null);
    closeAddSpellModal();
  };

  return (
    <Dialog
      open={ui.addSpellModalOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, height: 400 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {addFromGrimoire && unusedLevelUps === 0 && (
          <Alert severity="warning">No unused level-up available — learning from a grimoire requires one.</Alert>
        )}

        {!selectedWarbandId ? (
          <Typography variant="body2" color="text.secondary">
            No party selected.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {filteredAvailableSpells.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {addFromGrimoire
                  ? 'No grimoire spells available to add.'
                  : 'All available spells are already assigned to this party.'}
              </Typography>
            ) : (
              <>
                {!addFromGrimoire && (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="school-select-label">School</InputLabel>
                    <Select
                      labelId="school-select-label"
                      id="school-select"
                      value={selectedSchool}
                      label="School"
                      onChange={(e) => {
                        setSelectedSchool(e.target.value);
                        setSelectedSpellId('');
                      }}
                    >
                      {availableSchools.map((school) => (
                        <MenuItem key={school} value={school}>
                          <SpellSchoolChip label={school} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {addFromGrimoire && (
                  <Typography variant="subtitle1">Grimoires in your vault</Typography>
                )}
                {groupedSpells.map((group) => (
                  <Box key={group.school}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {group.spells.map((spell) => (
                        <SpellSchoolChip
                          key={spell.id}
                          label={spell.name}
                          school={spell.school}
                          clickable
                          color={selectedSpellId === spell.id ? 'school' : 'default'}
                          onClick={() => setSelectedSpellId(spell.id)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {selectedSpell ? (
                  <SpellCard spell={selectedSpell} />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, height: 250 }}>
                    Select a spell to view details
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={addFromGrimoire}
              onChange={(e) => {
                setAddFromGrimoire(e.target.checked);
                setSelectedSpellId('');
              }}
            />
          }
          label="Add from grimoire"
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSpell}
            disabled={!selectedSpellId || loading || !selectedWarbandId || (addFromGrimoire && unusedLevelUps === 0)}
          >
            {loading ? <CircularProgress size={24} /> : (unusedLevelUps > 0 ? 'Learn Spell' : 'Add Spell')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
